import { useQuery } from '@tanstack/react-query';
import { api, type ExitReason, type Problem } from '../api';
import { FieldGroup, RadioGroup, SelectField, TextField } from '../components/form';
import { Alert, Switch } from '../components/ui';
import type { Draft } from './draft';

const REASONS: { value: ExitReason; label: string }[] = [
  { value: 'RESIGNATION', label: 'Resignation' },
  { value: 'TERMINATION', label: 'Termination' },
  { value: 'CONTRACT_END', label: 'End of contract' },
  { value: 'RETIREMENT', label: 'Retirement' },
  { value: 'OTHER', label: 'Other' },
];

/** Who is leaving and when. The last working day is the field everything else
 *  keys off, so it gets the emphasis and the only hard validation. */
export function StepExit({
  draft,
  onChange,
  problems,
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  problems: Problem[];
}) {
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => api.departments() });

  const set = <K extends keyof Draft['employee']>(key: K, value: Draft['employee'][K]) =>
    onChange({ ...draft, employee: { ...draft.employee, [key]: value } });

  const started = draft.employee.fullName.trim().length > 0;
  const problemFor = (field: string) =>
    started ? problems.find((problem) => problem.field === field)?.message : undefined;

  const e = draft.employee;

  return (
    <div className="space-y-8">
      <FieldGroup title="Who is leaving" description="The person whose access is being withdrawn.">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <TextField
              label="Full name"
              required
              value={e.fullName}
              onChange={(value) => set('fullName', value)}
              error={problemFor('fullName')}
              maxLength={160}
            />
          </div>

          <TextField label="Job title" value={e.jobTitle} onChange={(value) => set('jobTitle', value)} maxLength={120} />

          <SelectField
            label="Department"
            required
            value={e.departmentId}
            onChange={(value) => set('departmentId', value)}
            error={problemFor('departmentId')}
            options={(departments.data?.departments ?? []).map((department) => ({
              value: department.id,
              label: department.name,
            }))}
          />

          <TextField
            label="Manager"
            value={e.managerName}
            onChange={(value) => set('managerName', value)}
            hint="Who to ask if something is unclear during the handover."
            maxLength={160}
          />

          <TextField
            label="Their email address"
            type="email"
            value={e.emailAddress}
            onChange={(value) => set('emailAddress', value)}
            hint="The account being closed."
            maxLength={160}
          />
        </div>
      </FieldGroup>

      <FieldGroup title="Exit" description="When access ends, and why they are leaving.">
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label="Last working day"
            type="date"
            required
            value={e.lastWorkingDay}
            onChange={(value) => set('lastWorkingDay', value)}
            error={problemFor('lastWorkingDay')}
            hint="Access is withdrawn at the end of this day unless the exit is immediate."
          />

          <div className="sm:col-span-2">
            <RadioGroup
              label="Reason for leaving"
              required
              value={e.exitReason}
              onChange={(value) => set('exitReason', value as ExitReason | '')}
              error={problemFor('exitReason')}
              options={REASONS}
            />
          </div>

          {e.exitReason === 'OTHER' && (
            <div className="sm:col-span-2">
              <TextField
                label="Reason"
                required
                value={e.exitReasonOther}
                onChange={(value) => set('exitReasonOther', value)}
                error={problemFor('exitReasonOther')}
                maxLength={200}
              />
            </div>
          )}
        </div>

        <div className="mt-5 flex items-start gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-800">
          <Switch
            id="immediate-exit"
            checked={e.immediateExit}
            onChange={(next) => set('immediateExit', next)}
            label="Cut access off immediately"
          />
          <div className="min-w-0">
            <label htmlFor="immediate-exit" className="block cursor-pointer text-sm font-medium text-slate-900 dark:text-white">
              Cut access off immediately
            </label>
            <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
              For a dismissal or anything contested. IT revokes on approval rather than waiting for the last
              working day.
            </p>
          </div>
        </div>

        {e.immediateExit && (
          <div className="mt-4">
            <Alert tone="warning">
              This will be treated as urgent. Tell IT directly as well as raising this: approval still has to
              happen, and an unwatched queue is not a fast one.
            </Alert>
          </div>
        )}
      </FieldGroup>
    </div>
  );
}
